import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import { styled } from '@mui/material/styles';

const CustomTimelineItem = styled(TimelineItem)(({ theme, isActive }) => ({
  '&:last-child .MuiTimelineConnector-root': {
    display: 'block',
  },
  '& .MuiTimelineContent-root': {
    color: isActive ? '#ffffff' : '#f5f5f5',
    fontWeight: isActive ? 'bold' : 'normal',
    transform: isActive ? 'scale(1.1)' : 'scale(1)',
    transition: 'all 0.3s ease-in-out',
  },
  '& .MuiTimelineDot-root': {
    backgroundColor: isActive ? '#ffffff' : 'transparent',
    transform: isActive ? 'scale(1.5)' : 'scale(1)',
    transition: 'all 0.3s ease-in-out',
  },
  '&:hover': {
    '& .MuiTimelineContent-root': {
      transform: 'scale(1.1)',
      fontWeight: 'bold',
      color: '#ffffff',
    },
    '& .MuiTimelineDot-root': {
      transform: 'scale(1.5)',
      borderColor: '#ffffff',
    },
    '& .MuiTimelineConnector-root': {
      transform: 'scaleX(1.5)',
      backgroundColor: '#ffffff',
    },
  },
}));

const CustomTimelineDot = styled(TimelineDot)(({ theme, filled, isActive }) => ({
  borderColor: '#f5f5f5',
  borderWidth: filled ? '3px' : '2px',
  backgroundColor: isActive ? '#ffffff' : (filled ? '#f5f5f5' : 'transparent'),
  padding: '4px',
  margin: '0',
  transition: 'all 0.3s ease-in-out',
  boxShadow: filled ? '0 0 0 2px rgba(245, 245, 245, 0.1)' : 'none',
  cursor: 'pointer',
}));

const CustomTimelineConnector = styled(TimelineConnector)(({ theme, filled, isActive }) => ({
  backgroundColor: isActive ? '#ffffff' : '#f5f5f5',
  width: filled || isActive ? '4px' : '3px', // Thicker connector
  margin: '0',
  transition: 'all 0.3s ease-in-out',
  transformOrigin: 'center left',
  boxShadow: filled || isActive ? '0 0 4px rgba(245, 245, 245, 0.2)' : 'none',
}));

const CustomTimelineContent = styled(TimelineContent)(({ theme, isActive }) => ({
  padding: '6px 16px',
  transition: 'all 0.3s ease-in-out',
  fontSize: '16px',
  color: isActive ? '#ffffff' : '#f5f5f5',
  fontWeight: isActive ? 'bold' : 'normal',
  cursor: 'pointer',
  textAlign: 'center', // Center align content
}));

const CustomTimelineSeparator = styled(TimelineSeparator)(({ theme }) => ({
  transition: 'transform 0.3s ease-in-out',
}));

const OutlinedTimeline = ({ events, fillPercentage = 0.45 }) => {
  const timelineRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const getFillIndex = () => Math.floor(events.length * fillPercentage);

  useEffect(() => {
    const scrollToFillUntil = () => {
      const fillIndex = getFillIndex();
      if (fillIndex !== -1 && timelineRef.current) {
        const timelineItems = timelineRef.current.querySelectorAll('.MuiTimelineItem-root');
        if (timelineItems[fillIndex]) {
          timelineItems[fillIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    };

    setTimeout(scrollToFillUntil, 500);
  }, [fillPercentage, events]);

  const handleClick = (event) => {
    const route = `/${event.toLowerCase().replace(/\s+/g, '-')}`;
    navigate(route);
  };

  return (
    <div className="timeline-container" ref={timelineRef}>
      <Timeline position="right" className="scrollable-timeline">
        {events.map((event, index) => {
          const filled = index < getFillIndex();
          const route = `/${event.toLowerCase().replace(/\s+/g, '-')}`;
          const isActive = location.pathname === route;

          return (
            <CustomTimelineItem key={index} isActive={isActive}>
              <CustomTimelineSeparator>
                <CustomTimelineDot
                  variant="outlined"
                  filled={filled ? 1 : 0}
                  onClick={() => handleClick(event)}
                  tabIndex="0"
                  isActive={isActive}
                />
                {index < events.length - 1 && (
                  <CustomTimelineConnector filled={filled ? 1 : 0} isActive={isActive} />
                )}
              </CustomTimelineSeparator>
              <CustomTimelineContent onClick={() => handleClick(event)} isActive={isActive}>
                {event}
              </CustomTimelineContent>
            </CustomTimelineItem>
          );
        })}
      </Timeline>
    </div>
  );
};

export default OutlinedTimeline;
